<!-- FieldExpenseReportsPanel.vue - Panel de reportes de gastos para supervisores y otros roles -->
<template>
  <div class="field-expense-reports-panel">
    <!-- Header compacto con título, período y acciones -->
    <v-card class="mb-4" variant="flat">
      <v-card-title class="d-flex align-center flex-wrap ga-2 pa-3">
        <v-btn
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          @click="$router.back()"
          class="mr-1"
        ></v-btn>
        <v-icon color="primary" size="24">mdi-chart-box</v-icon>
        <span class="text-h6 ml-1">Reportes de Gastos</span>
        <v-chip 
          v-if="filters.fecha_desde && filters.fecha_hasta"
          color="primary" 
          variant="tonal" 
          size="small"
          class="ml-2"
          prepend-icon="mdi-calendar"
        >
          {{ formatDateShort(filters.fecha_desde) }} - {{ formatDateShort(filters.fecha_hasta) }}
        </v-chip>
        <v-spacer></v-spacer>
        <!--
          P13-V5A-R5: el control decía "Exportar" y el icono anunciaba Excel, pero `exportToExcel`
          escribe un CSV con `expenses.value`, es decir sólo las filas de la página cargada. Un
          archivo que se descarga como si fuera el reporte del período y contiene 20 de N filas es
          una salida de negocio falsa. No se implementa la exportación completa (sería una
          capacidad nueva: recorrer todas las páginas): el control dice lo que hace.
        -->
        <v-btn
          color="success"
          variant="tonal"
          size="small"
          prepend-icon="mdi-file-delimited"
          class="mr-2"
          @click="exportToExcel"
          :loading="exporting"
          :disabled="!expenses.length"
          data-testid="expense-export-page-csv"
        >
          Exportar página (CSV)
        </v-btn>
        <v-btn
          icon="mdi-refresh"
          variant="text"
          size="small"
          @click="loadExpenses"
          :loading="loading"
        ></v-btn>
      </v-card-title>
    </v-card>

    <!--
      P13-V5A-R5: una lectura fallida se declara. Antes sólo pasaba un snackbar de 3 segundos y la
      pantalla se quedaba con las cifras de la consulta anterior como si fueran las de ahora.
    -->
    <v-alert
      v-if="loadError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-4"
      data-testid="expense-reports-load-error"
    >
      No se pudieron cargar los gastos. Las cifras de esta pantalla no están disponibles: lo que ves
      no es una medición de 0, es una lectura que falló. Vuelve a intentarlo con el botón de recarga.
    </v-alert>

    <!-- KPIs Resumen -->
    <!--
      P13-V5A-R5 · ALCANCE DECLARADO. Las tres cifras de dinero se leían como el gasto del período
      filtrado, pero `totals` suma `expenses`, que sólo contiene la PÁGINA cargada: `loadExpenses`
      pide `{ page, limit: itemsPerPage }` (20 por defecto) y el backend devuelve exactamente esa
      página. Ni `GET /field-supervisor/expenses` ni `GET /super-admin/expenses` publican una suma
      del conjunto: su respuesta lleva sólo `pagination { current_page, per_page, total_items,
      total_pages }` (backend `src/routes/field-supervisor.js` y `src/routes/superAdminRoutes.js`,
      ambos con `GroupExpense.findAndCountAll`, que devuelve filas y recuento, nunca un SUM). Es
      decir: NO existe productor canónico de un total del período, y recorrer el resto de páginas
      sería una capacidad nueva. Se declara entonces el alcance real de cada cifra en vez de
      dejar un número que contradice al recuento que tiene al lado. El recuento sí lo publica el
      servidor para todo el conjunto (`pagination.total_items`), así que esa tarjeta es la única
      que describe el período completo y se etiqueta como tal.
    -->
    <v-row class="mb-4">
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="primary">
          <v-card-text class="text-center pa-4">
            <div class="text-h5 font-weight-bold text-primary" data-testid="expense-total-page">
              RD$ {{ formatNumber(totals.total) }}
            </div>
            <div class="text-caption text-medium-emphasis">Total Gastado (esta página)</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="info">
          <v-card-text class="text-center pa-4">
            <div class="text-h5 font-weight-bold text-info">
              RD$ {{ formatNumber(totals.grupos) }}
            </div>
            <div class="text-caption text-medium-emphasis">Gastos Grupos (esta página)</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="orange">
          <v-card-text class="text-center pa-4">
            <div class="text-h5 font-weight-bold text-orange">
              RD$ {{ formatNumber(totals.fondo) }}
            </div>
            <div class="text-caption text-medium-emphasis">Del Fondo Supervisor (esta página)</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="grey">
          <v-card-text class="text-center pa-4">
            <!--
              P13-V5A-R5: mostraba `expenses.length`, el tamaño de la página, bajo la etiqueta
              "Registros". El recuento del conjunto sí lo reporta el servidor, así que aquí se usa
              ese campo; si la respuesta no lo trae, es un dato ausente y se dice, no se sustituye
              por el tamaño de la página. Un recuento real de 0 sigue mostrándose como 0.
            -->
            <div class="text-h5 font-weight-bold" data-testid="expense-record-count">{{ totalItemsLabel }}</div>
            <div class="text-caption text-medium-emphasis">Registros (período filtrado)</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!--
      P13-V5A-R5: la contradicción que hacía creíble el total (un importe de una página junto a un
      recuento de todo el período) ahora se explica en la propia pantalla siempre que la página sea
      un subconjunto. No se recalcula nada: sólo se declara qué se está sumando.
    -->
    <v-alert
      v-if="pageIsSubset"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
      data-testid="expense-totals-page-scope"
    >
      Los importes y las distribuciones de esta pantalla suman únicamente los {{ expenses.length }}
      registros cargados (página {{ pagination.current_page }}). El período filtrado tiene
      {{ totalItemsLabel }} registros y el backend no publica una suma del conjunto, por lo que no
      se muestra un total del período.
    </v-alert>

    <!-- Filtros -->
    <v-card class="mb-4" variant="outlined">
      <v-card-text>
        <v-row dense>
          <!-- Filtro de Rango de Fechas -->
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.dateRange"
              :items="dateRangeOptions"
              item-title="label"
              item-value="value"
              label="Período"
              density="compact"
              hide-details
              prepend-inner-icon="mdi-calendar-range"
              @update:model-value="onDateRangeChange"
            ></v-select>
          </v-col>

          <!-- Fecha Desde (solo si es personalizado) -->
          <v-col cols="6" sm="3" md="2" v-if="filters.dateRange === 'custom'">
            <v-text-field
              v-model="filters.fecha_desde"
              label="Desde"
              type="date"
              density="compact"
              hide-details
            ></v-text-field>
          </v-col>

          <!-- Fecha Hasta (solo si es personalizado) -->
          <v-col cols="6" sm="3" md="2" v-if="filters.dateRange === 'custom'">
            <v-text-field
              v-model="filters.fecha_hasta"
              label="Hasta"
              type="date"
              density="compact"
              hide-details
            ></v-text-field>
          </v-col>

          <!-- Filtro por Grupo -->
          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="filters.group_id"
              :items="groups"
              item-title="name"
              item-value="id"
              label="Grupo"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-account-group"
              @update:model-value="onGroupChange"
            ></v-autocomplete>
          </v-col>

          <!-- Filtro por Agencia (dependiente del grupo) -->
          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="filters.agency_id"
              :items="filteredAgencies"
              item-title="name"
              item-value="id"
              label="Agencia"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-store"
              :disabled="loadingAgencies"
            ></v-autocomplete>
          </v-col>

          <!-- Filtro por Tipo de Gasto -->
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.tipo_gasto"
              :items="tipoGastoOptions"
              item-title="label"
              item-value="value"
              label="Tipo"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-tag"
            ></v-select>
          </v-col>

          <!-- Filtro por Categoría -->
          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="filters.categoria"
              :items="categorias"
              label="Categoría"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-shape"
            ></v-autocomplete>
          </v-col>

          <!-- Botón Aplicar Filtros -->
          <v-col cols="12" sm="6" md="2" class="d-flex align-center">
            <v-btn
              color="primary"
              variant="tonal"
              block
              @click="loadExpenses"
              :loading="loading"
            >
              <v-icon start>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Gráficos de Distribución -->
    <v-row class="mb-4" v-if="expenses.length > 0">
      <!-- Distribución por Categoría -->
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <!--
            P13-V5A-R5: los porcentajes se calculan sobre `expenses`, la página cargada, no sobre
            el período. Una distribución de una página no es la distribución del conjunto, así que
            el título dice sobre qué se calculó. Las barras siguen dibujándose porque cada valor sí
            está medido: lo que era falso era el alcance que se le atribuía.
          -->
          <v-card-title class="d-flex align-center text-subtitle-1">
            <v-icon start size="small">mdi-chart-pie</v-icon>
            Distribución por Categoría (esta página)
            <v-spacer></v-spacer>
            <v-chip 
              v-if="categoryDistribution.length > 5" 
              size="x-small" 
              color="grey" 
              variant="tonal"
            >
              {{ categoryDistribution.length }} categorías
            </v-chip>
          </v-card-title>
          <v-card-text class="pt-0">
            <div class="category-chart">
              <div 
                v-for="(cat, index) in categoryDistribution" 
                :key="cat.categoria"
                class="category-bar mb-2"
              >
                <div class="d-flex justify-space-between mb-1">
                  <span class="text-caption text-truncate category-label">{{ cat.categoria || 'Sin categoría' }}</span>
                  <span class="text-caption font-weight-medium text-no-wrap ml-2">
                    RD$ {{ formatNumber(cat.total) }} ({{ cat.percentage }}%)
                  </span>
                </div>
                <v-progress-linear
                  :model-value="cat.percentage"
                  :color="chartColors[index % chartColors.length]"
                  height="8"
                  rounded
                ></v-progress-linear>
              </div>
            </div>
            <!-- Indicador de scroll si hay muchas categorías -->
            <div v-if="categoryDistribution.length > 6" class="text-center mt-2">
              <v-icon size="small" color="grey">mdi-chevron-down</v-icon>
              <span class="text-caption text-grey ml-1">Desliza para ver más</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Distribución por Tipo -->
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <!-- P13-V5A-R5: mismo alcance que la tarjeta anterior — fijo/variable sobre la página. -->
          <v-card-title class="text-subtitle-1">
            <v-icon start size="small">mdi-chart-donut</v-icon>
            Distribución por Tipo (esta página)
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <div class="text-center pa-4">
                  <v-progress-circular
                    :model-value="typeDistribution.fijo.percentage"
                    :size="100"
                    :width="12"
                    color="info"
                  >
                    <span class="text-caption font-weight-bold">{{ typeDistribution.fijo.percentage }}%</span>
                  </v-progress-circular>
                  <div class="mt-2 text-caption">Gastos Fijos</div>
                  <div class="text-subtitle-2 font-weight-bold text-info">
                    RD$ {{ formatNumber(typeDistribution.fijo.total) }}
                  </div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-center pa-4">
                  <v-progress-circular
                    :model-value="typeDistribution.variable.percentage"
                    :size="100"
                    :width="12"
                    color="orange"
                  >
                    <span class="text-caption font-weight-bold">{{ typeDistribution.variable.percentage }}%</span>
                  </v-progress-circular>
                  <div class="mt-2 text-caption">Gastos Variables</div>
                  <div class="text-subtitle-2 font-weight-bold text-orange">
                    RD$ {{ formatNumber(typeDistribution.variable.total) }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de Gastos -->
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center">
        <v-icon start>mdi-table</v-icon>
        Detalle de Gastos
        <v-spacer></v-spacer>
        <!-- P13-V5A-R5: `total_items` es el recuento del conjunto; ausente se dice, no se supone. -->
        <v-chip size="small" color="primary" variant="tonal">
          <template v-if="totalItemsCount !== null">{{ totalItemsLabel }} registros</template>
          <template v-else>Registros: sin datos</template>
        </v-chip>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="expenses"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :page="currentPage"
        class="elevation-0"
        density="compact"
        hover
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
      >
        <!-- Fecha -->
        <template #item.fecha_gasto="{ item }">
          <span class="text-caption">{{ formatDate(item.fecha_gasto) }}</span>
        </template>

        <!-- Concepto -->
        <template #item.concepto="{ item }">
          <div class="d-flex align-center">
            <v-icon 
              :icon="item.tipo_gasto === 'fijo' ? 'mdi-calendar-clock' : 'mdi-cash-fast'" 
              :color="item.tipo_gasto === 'fijo' ? 'info' : 'orange'"
              size="small"
              class="mr-2"
            ></v-icon>
            <div>
              <div class="font-weight-medium">{{ item.concepto }}</div>
              <div class="text-caption text-medium-emphasis">{{ item.categoria }}</div>
            </div>
          </div>
        </template>

        <!-- Tipo -->
        <template #item.tipo_gasto="{ item }">
          <v-chip 
            :color="item.tipo_gasto === 'fijo' ? 'info' : 'orange'" 
            size="x-small"
            variant="tonal"
          >
            {{ item.tipo_gasto === 'fijo' ? 'Fijo' : 'Variable' }}
          </v-chip>
        </template>

        <!-- Monto -->
        <template #item.monto="{ item }">
          <span class="font-weight-bold">RD$ {{ formatNumber(item.monto) }}</span>
        </template>

        <!-- Ubicación (Grupo + Agencia combinados) -->
        <template #item.ubicacion="{ item }">
          <div class="ubicacion-cell">
            <div class="text-caption font-weight-medium text-truncate" :title="item.group?.name || '-'">
              {{ item.group?.name || '-' }}
            </div>
            <div 
              v-if="item.agency?.name" 
              class="text-caption text-medium-emphasis text-truncate agency-name"
              :title="item.agency?.name"
            >
              {{ item.agency?.name }}
            </div>
          </div>
        </template>

        <!-- Estado -->
        <template #item.estado="{ item }">
          <v-chip 
            :color="getEstadoColor(item.estado)" 
            size="x-small"
            variant="tonal"
          >
            {{ getEstadoLabel(item.estado) }}
          </v-chip>
        </template>

        <!-- Documento -->
        <template #item.tiene_documento="{ item }">
          <v-icon 
            :icon="item.tiene_documento ? 'mdi-file-check' : 'mdi-file-remove'" 
            :color="item.tiene_documento ? 'success' : 'grey'"
            size="small"
          ></v-icon>
        </template>

        <!-- Sin datos -->
        <template #no-data>
          <div class="text-center pa-8">
            <v-icon size="64" color="grey-lighten-1">mdi-file-search-outline</v-icon>
            <div class="text-h6 mt-4 text-grey">No hay gastos para mostrar</div>
            <div class="text-caption text-grey">Ajusta los filtros para ver resultados</div>
          </div>
        </template>

        <!-- Loading -->
        <template #loading>
          <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import ApiClient from '@/services/api/api-client'

// Props - permite que otros roles usen este componente
const props = defineProps({
  // Si se pasa un groupId específico, filtra solo ese grupo
  groupId: {
    type: Number,
    default: null
  },
  // Si se pasa un supervisorId, obtiene los grupos de ese supervisor
  supervisorId: {
    type: Number,
    default: null
  },
  // Modo SuperAdmin: acceso a TODOS los grupos del sistema
  isSuperAdmin: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['loaded'])

// State
const loading = ref(false)
// P13-V5A-R5: estado explícito de lectura fallida. El snackbar dura 3 s; el hecho de que la
// pantalla no tenga datos medidos dura hasta la siguiente lectura correcta.
const loadError = ref(false)
const loadingAgencies = ref(false)
const exporting = ref(false)
const expenses = ref([])
const groups = ref([])
const allAgencies = ref([])
const categorias = ref([])

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(20)
// P13-V5A-R5: el estado inicial declaraba `total_items: 0` antes de leer nada, y ese 0 es una
// afirmación ("no hay gastos") que nadie midió. Antes de la primera respuesta el recuento es
// desconocido, no cero; un 0 devuelto por el servidor sigue siendo un 0 real y se muestra.
const pagination = ref({
  current_page: 1,
  total_pages: null,
  total_items: null
})

// Filters
const filters = ref({
  dateRange: 'month',
  fecha_desde: null,
  fecha_hasta: null,
  group_id: null,
  agency_id: null,
  tipo_gasto: null,
  categoria: null
})

// Options
const dateRangeOptions = [
  { label: 'Hoy', value: 'today' },
  { label: 'Esta Semana', value: 'week' },
  { label: 'Este Mes', value: 'month' },
  { label: 'Último Trimestre', value: 'quarter' },
  { label: 'Este Año', value: 'year' },
  { label: 'Personalizado', value: 'custom' }
]

const tipoGastoOptions = [
  { label: 'Fijos', value: 'fijo' },
  { label: 'Variables', value: 'variable' }
]

const chartColors = ['primary', 'info', 'success', 'warning', 'error', 'purple', 'teal', 'indigo']

// Table headers optimizados para mejor uso del espacio
const tableHeaders = [
  { title: 'Fecha', key: 'fecha_gasto', width: '85px' },
  { title: 'Concepto', key: 'concepto', minWidth: '180px' },
  { title: 'Tipo', key: 'tipo_gasto', width: '75px' },
  { title: 'Monto', key: 'monto', width: '100px', align: 'end' },
  { title: 'Ubicación', key: 'ubicacion', minWidth: '200px' },
  { title: 'Estado', key: 'estado', width: '85px' },
  { title: 'Doc', key: 'tiene_documento', width: '50px', align: 'center' }
]

// Snackbar
const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

// Computed: Agencias filtradas por grupo seleccionado
const filteredAgencies = computed(() => {
  if (!filters.value.group_id) {
    return allAgencies.value
  }
  return allAgencies.value.filter(a => a.groupId === filters.value.group_id)
})

// P13-V5A-R5 · el recuento del conjunto es lo único de alcance completo que publica el productor.
// `total_items` sale de `findAndCountAll` en el backend y describe el período filtrado entero.
// Si no viene (respuesta sin bloque `pagination`, o lectura fallida), es un dato AUSENTE: no se
// sustituye por `expenses.length`, que es el tamaño de la página.
const totalItemsCount = computed(() => {
  const raw = pagination.value?.total_items
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
})

const totalItemsLabel = computed(() => (
  totalItemsCount.value === null
    ? 'Sin datos'
    : new Intl.NumberFormat('es-DO', { maximumFractionDigits: 0 }).format(totalItemsCount.value)
))

// La página es un subconjunto sólo si el servidor dijo cuántos registros hay y son más de los
// que tenemos cargados. Sin recuento del servidor no se afirma ni lo uno ni lo otro.
const pageIsSubset = computed(() => (
  totalItemsCount.value !== null && totalItemsCount.value > expenses.value.length
))

// Computed: Totales para KPIs
// P13-V5A-R5: suma SÓLO las filas cargadas (la página). Se conserva porque cada monto está
// medido, pero la plantilla ya no lo presenta como el total del período. Ver el comentario de la
// fila de KPIs: el backend no expone ninguna suma del conjunto.
const totals = computed(() => {
  const result = {
    total: 0,
    grupos: 0,  // Gastos pagados por empresa (del grupo)
    fondo: 0    // Gastos del fondo del supervisor
  }
  
  expenses.value.forEach(e => {
    const monto = parseFloat(e.monto) || 0
    result.total += monto
    
    if (e.fuente_financiamiento === 'empresa') {
      result.grupos += monto
    } else {
      result.fondo += monto
    }
  })
  
  return result
})

// Computed: Distribución por categoría
const categoryDistribution = computed(() => {
  const byCategory = {}
  let total = 0
  
  expenses.value.forEach(e => {
    const cat = e.categoria || 'Sin categoría'
    const monto = parseFloat(e.monto) || 0
    byCategory[cat] = (byCategory[cat] || 0) + monto
    total += monto
  })
  
  return Object.entries(byCategory)
    .map(([categoria, catTotal]) => ({
      categoria,
      total: catTotal,
      percentage: total > 0 ? Math.round((catTotal / total) * 100) : 0
    }))
    .sort((a, b) => b.total - a.total)
    // Sin límite - mostrar todas las categorías con scroll
})

// Computed: Distribución por tipo
const typeDistribution = computed(() => {
  let fijoTotal = 0
  let variableTotal = 0
  
  expenses.value.forEach(e => {
    const monto = parseFloat(e.monto) || 0
    if (e.tipo_gasto === 'fijo') {
      fijoTotal += monto
    } else {
      variableTotal += monto
    }
  })
  
  const total = fijoTotal + variableTotal
  
  return {
    fijo: {
      total: fijoTotal,
      percentage: total > 0 ? Math.round((fijoTotal / total) * 100) : 0
    },
    variable: {
      total: variableTotal,
      percentage: total > 0 ? Math.round((variableTotal / total) * 100) : 0
    }
  }
})

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatDateShort = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('es-DO', { day: '2-digit', month: 'short' })
}

const getEstadoColor = (estado) => {
  const colors = {
    'aprobado': 'success',
    'pendiente': 'warning',
    'rechazado': 'error'
  }
  return colors[estado] || 'grey'
}

const getEstadoLabel = (estado) => {
  const labels = {
    'aprobado': 'Aprobado',
    'pendiente': 'Pendiente',
    'rechazado': 'Rechazado'
  }
  return labels[estado] || estado
}

const onDateRangeChange = (range) => {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  
  switch (range) {
    case 'today':
      filters.value.fecha_desde = `${yyyy}-${mm}-${dd}`
      filters.value.fecha_hasta = `${yyyy}-${mm}-${dd}`
      break
    case 'week':
      const weekStart = new Date(today)
      weekStart.setDate(today.getDate() - today.getDay())
      filters.value.fecha_desde = weekStart.toISOString().split('T')[0]
      filters.value.fecha_hasta = `${yyyy}-${mm}-${dd}`
      break
    case 'month':
      filters.value.fecha_desde = `${yyyy}-${mm}-01`
      filters.value.fecha_hasta = `${yyyy}-${mm}-${dd}`
      break
    case 'quarter':
      const quarterStart = new Date(today)
      quarterStart.setMonth(today.getMonth() - 3)
      filters.value.fecha_desde = quarterStart.toISOString().split('T')[0]
      filters.value.fecha_hasta = `${yyyy}-${mm}-${dd}`
      break
    case 'year':
      filters.value.fecha_desde = `${yyyy}-01-01`
      filters.value.fecha_hasta = `${yyyy}-${mm}-${dd}`
      break
    case 'custom':
      // No cambiar fechas, el usuario las seleccionará
      break
  }
  
  if (range !== 'custom') {
    loadExpenses()
  }
}

const onGroupChange = (groupId) => {
  // Limpiar agencia si el grupo cambió
  filters.value.agency_id = null
  
  if (groupId) {
    loadAgenciesForGroup(groupId)
  }
  
  // Aplicar filtro automáticamente
  loadExpenses()
}

const loadGroups = async () => {
  try {
    // SuperAdmin usa endpoint global, FieldSupervisor usa su endpoint específico
    const endpoint = props.isSuperAdmin 
      ? '/super-admin/groups'  // Endpoint global: todos los grupos
      : '/field-supervisor/groups'  // Endpoint del supervisor: solo sus grupos
    
    const response = await ApiClient.get(endpoint)
    const data = response?.data || response
    
    // Normalizar respuesta
    groups.value = data?.groups || []
    
    // Extraer todas las agencias de los grupos
    const agencies = []
    groups.value.forEach(g => {
      // Las agencias pueden venir como 'points' o 'agencies'
      const groupAgencies = g.agencies || g.points || []
      if (Array.isArray(groupAgencies)) {
        groupAgencies.forEach(a => {
          agencies.push({ ...a, groupId: g.id })
        })
      }
    })
    allAgencies.value = agencies
    
  } catch (error) {
    console.error('Error cargando grupos:', error)
  }
}

const loadAgenciesForGroup = async (groupId) => {
  loadingAgencies.value = true
  try {
    // Las agencias ya están en allAgencies, solo filtramos
    // Si necesitamos cargar específicamente, podemos hacer una llamada
    loadingAgencies.value = false
  } catch (error) {
    console.error('Error cargando agencias:', error)
    loadingAgencies.value = false
  }
}

const loadCategorias = async () => {
  try {
    // SuperAdmin usa su endpoint, FieldSupervisor usa el suyo
    const endpoint = props.isSuperAdmin
      ? '/super-admin/expenses-categories'
      : '/field-supervisor/expenses-categories'
    
    const response = await ApiClient.get(endpoint)
    const data = response?.data || response
    // El endpoint devuelve { fijas, variables, todas }
    const allCats = data?.todas || data?.categories || []
    // Usar el value para filtrar en el backend
    categorias.value = allCats.map(c => c.value)
  } catch (error) {
    console.error('Error cargando categorías:', error)
    // Fallback: categorías sincronizadas con FieldVariableExpensesPanel.vue
    categorias.value = [
      // Gastos Fijos
      'alquiler', 'servicios_basicos', 'mantenimiento',
      // Gastos Variables
      'transporte', 'alimentacion', 'materiales', 
      'comunicacion', 'tecnico', 'emergencia',
      // Genérico
      'otros'
    ]
  }
}

const loadExpenses = async () => {
  loading.value = true
  loadError.value = false
  try {
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value
    }
    
    if (filters.value.group_id) params.group_id = filters.value.group_id
    if (filters.value.agency_id) params.agency_id = filters.value.agency_id
    if (filters.value.tipo_gasto) params.tipo_gasto = filters.value.tipo_gasto
    if (filters.value.categoria) params.categoria = filters.value.categoria
    if (filters.value.fecha_desde) params.fecha_desde = filters.value.fecha_desde
    if (filters.value.fecha_hasta) params.fecha_hasta = filters.value.fecha_hasta
    
    // SuperAdmin usa endpoint global, FieldSupervisor usa su endpoint específico
    const endpoint = props.isSuperAdmin 
      ? '/super-admin/expenses'  // Endpoint global: todos los gastos
      : '/field-supervisor/expenses'  // Endpoint del supervisor
    
    const response = await ApiClient.get(endpoint, { params })
    const data = response?.data || response
    
    expenses.value = data?.expenses || []
    // P13-V5A-R5: el respaldo anterior escribía `total_items: expenses.value.length`, o sea
    // declaraba el tamaño de la página como el recuento del período cuando el productor no había
    // dicho nada. Sin bloque `pagination` el recuento es desconocido y así se muestra.
    pagination.value = data?.pagination || {
      current_page: currentPage.value,
      total_pages: null,
      total_items: null
    }

    // P13-V5A-R5: el evento publicaba `totals` a secas; el nombre ahora dice que es la suma de la
    // página cargada, para que ningún consumidor futuro lo tome por el total del período.
    emit('loaded', { expenses: expenses.value, page_totals: totals.value })

  } catch (error) {
    console.error('Error cargando gastos:', error)
    // P13-V5A-R5: una lectura fallida no es una medición. Antes se conservaban las filas y el
    // recuento de la consulta ANTERIOR, que quedaban en pantalla bajo los filtros nuevos en
    // cuanto el snackbar (3 s) desaparecía. Se descartan y el fallo queda visible.
    expenses.value = []
    pagination.value = { current_page: currentPage.value, total_pages: null, total_items: null }
    loadError.value = true
    snackbar.value = {
      show: true,
      message: 'Error al cargar los gastos',
      color: 'error'
    }
  } finally {
    loading.value = false
  }
}

const onPageChange = (page) => {
  currentPage.value = page
  loadExpenses()
}

const onItemsPerPageChange = (perPage) => {
  itemsPerPage.value = perPage
  currentPage.value = 1
  loadExpenses()
}

// P13-V5A-R5 · la exportación dice qué exporta.
//
// `expenses.value` es la página cargada (`limit: itemsPerPage`), nunca el período filtrado; el
// archivo se llamaba `reporte_gastos_<fecha>.csv` y el mensaje decía "Reporte exportado
// correctamente", así que un CSV de 20 filas se guardaba como si fuera el reporte completo. No se
// implementa la exportación del conjunto: exigiría recorrer todas las páginas, que es una
// capacidad nueva y cara, y el backend no ofrece un endpoint de exportación. El nombre del archivo
// y el mensaje declaran el alcance real.
const exportToExcel = async () => {
  exporting.value = true
  try {
    // Crear CSV para exportar
    const headers = ['Fecha', 'Concepto', 'Categoría', 'Tipo', 'Monto', 'Grupo', 'Agencia', 'Estado']
    const rows = expenses.value.map(e => [
      e.fecha_gasto || '',
      e.concepto || '',
      e.categoria || '',
      e.tipo_gasto === 'fijo' ? 'Fijo' : 'Variable',
      e.monto || 0,
      e.group?.name || '',
      e.agency?.name || '',
      e.estado || ''
    ])
    
    // Crear contenido CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(','))
    ].join('\n')
    
    // Descargar archivo
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `gastos_pagina_${pagination.value?.current_page || currentPage.value}_${new Date().toISOString().split('T')[0]}.csv`
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    snackbar.value = {
      show: true,
      message: pageIsSubset.value
        ? `Se exportaron ${rows.length} registros: sólo la página ${pagination.value?.current_page || currentPage.value}, no el período completo (${totalItemsLabel.value} registros).`
        : `Se exportaron ${rows.length} registros de la página actual.`,
      color: 'success'
    }
  } catch (error) {
    console.error('Error exportando:', error)
    snackbar.value = {
      show: true,
      message: 'Error al exportar el reporte',
      color: 'error'
    }
  } finally {
    exporting.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Inicializar fechas con el mes actual
  onDateRangeChange('month')
  
  // Cargar datos iniciales
  await Promise.all([
    loadGroups(),
    loadCategorias()
  ])
  
  // Si hay groupId en props, establecerlo
  if (props.groupId) {
    filters.value.group_id = props.groupId
  }
  
  // Cargar gastos
  await loadExpenses()
})

// Watch para prop groupId (desde componente padre)
watch(() => props.groupId, (newGroupId) => {
  if (newGroupId) {
    filters.value.group_id = newGroupId
    loadExpenses()
  }
})

// Watch para filtros que aplican automáticamente (excepto group_id que usa onGroupChange)
watch(() => filters.value.agency_id, () => {
  loadExpenses()
})

watch(() => filters.value.tipo_gasto, () => {
  loadExpenses()
})

watch(() => filters.value.categoria, () => {
  loadExpenses()
})
</script>

<style scoped>
.field-expense-reports-panel {
  padding: 16px;
}

.category-chart {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
  /* Scrollbar sutil */
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}

.category-chart::-webkit-scrollbar {
  width: 6px;
}

.category-chart::-webkit-scrollbar-track {
  background: transparent;
}

.category-chart::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 3px;
}

.category-bar {
  padding: 4px 0;
}

.category-label {
  max-width: 150px;
}

/* Responsive: en móvil, altura adaptable */
@media (max-width: 600px) {
  .category-chart {
    max-height: 220px;
  }
  
  .field-expense-reports-panel {
    padding: 8px;
  }
}

/* Estilos para columna de ubicación */
.ubicacion-cell {
  max-width: 200px;
  line-height: 1.3;
}

.ubicacion-cell .agency-name {
  max-width: 100%;
  font-size: 0.75rem;
}

/* Truncate para textos largos con tooltip */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mejoras para tabla compacta */
:deep(.v-data-table) {
  font-size: 0.875rem;
}

:deep(.v-data-table td) {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

:deep(.v-data-table th) {
  font-weight: 600 !important;
  white-space: nowrap;
}
</style>
